import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactDOM from "react-dom";
import {
  fetchUserInactiveShipments,
  formatInactiveShipmentDateTime,
} from "../../utils/inactiveShipmentAlert";

const InactiveShipmentButton = ({
  user,
  shipmentsPath = "",
  className = "",
  title = "Inactive Shipments",
}) => {
  const navigate = useNavigate();
  const userId = Number(user?.user_id || user?.id || 0);
  const [open, setOpen] = useState(false);
  const [shipments, setShipments] = useState([]);

  const loadInactiveShipments = async () => {
    if (!userId) return;
    try {
      const data = await fetchUserInactiveShipments(userId);
      setShipments(data);
    } catch (error) {
      console.error("Failed to fetch inactive shipments:", error);
    }
  };

  useEffect(() => {
    loadInactiveShipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const previewShipments = useMemo(() => shipments.slice(0, 5), [shipments]);
  const remainingCount = shipments.length - previewShipments.length;
  const hasInactiveShipments = shipments.length > 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`relative flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-amber-600 shadow-sm transition hover:bg-amber-50 hover:text-amber-700 ${className}`}
        aria-label={title}
        title={title}
      >
        <i className="fas fa-triangle-exclamation text-base" />
        {hasInactiveShipments ? (
          <span className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />
        ) : null}
      </button>

      {open
        ? ReactDOM.createPortal(
            <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/45 p-3 sm:p-4 backdrop-blur-[2px]">
              <div className="flex max-h-[min(92vh,760px)] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-5 sm:px-6">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                      Shipment Alert
                    </p>
                    <h2 className="mt-1 text-xl font-bold text-slate-900 sm:text-2xl">
                      Inactive shipments
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                      Shipments assigned to your account that have not been updated
                      for more than 24 hours.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
                    aria-label="Close inactive shipment list"
                  >
                    <i className="fa-solid fa-times" />
                  </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
                  {hasInactiveShipments ? (
                    <>
                      <div className="mb-4 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        <i className="fa-solid fa-triangle-exclamation text-amber-600" />
                        <span>
                          {shipments.length} inactive shipment
                          {shipments.length > 1 ? "s" : ""} on your account
                        </span>
                      </div>

                      <div className="space-y-3">
                        {previewShipments.map((shipment) => (
                          <div
                            key={shipment.shipment_id}
                            className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                          >
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                              <div className="min-w-0">
                                <p className="font-semibold text-slate-900">
                                  {shipment.tracking_no ||
                                    shipment.job_order ||
                                    `Shipment ${shipment.shipment_id}`}
                                </p>
                                <p className="break-words text-sm text-slate-600">
                                  {shipment.shipper_name ||
                                    shipment.receiver_name ||
                                    "Unnamed shipment"}
                                </p>
                                <p className="mt-1 text-xs uppercase tracking-[0.12em] text-slate-500">
                                  Status: {shipment.status || "Unknown"}
                                </p>
                              </div>
                              <div className="text-sm text-slate-600 sm:shrink-0 sm:text-right">
                                <p>Last update</p>
                                <p className="font-medium text-slate-800">
                                  {formatInactiveShipmentDateTime(
                                    shipment.status_updated_at
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {remainingCount > 0 ? (
                        <p className="mt-4 text-sm text-slate-500">
                          And {remainingCount} more inactive shipment
                          {remainingCount > 1 ? "s" : ""}.
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
                      No inactive shipments on your account.
                    </div>
                  )}
                </div>

                <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                  >
                    Close
                  </button>
                  {shipmentsPath ? (
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        navigate(shipmentsPath);
                      }}
                      className="rounded-2xl bg-blue px-5 py-3 text-sm font-semibold text-white transition hover:opacity-95"
                    >
                      View Shipments
                    </button>
                  ) : null}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default InactiveShipmentButton;
